import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { VideoFeatures } from '@/store/videoFeatures';
import { LivePreviewPanel } from './LivePreviewPanel';
import { FeatureConfigHeader } from './FeatureConfigHeader';
import { FeatureConfigFooter } from './FeatureConfigFooter';
import { useLeftAnchoredModal } from '@/hooks/useLeftAnchoredModal';

// Feature config components
import { VideoControlsConfigurator } from '@/components/feature-configs/VideoControlsConfigurator';
import { HeadlineOverlayConfig } from '@/components/feature-configs/headline-overlay';
import { ThumbnailConfig } from '@/components/feature-configs/ThumbnailConfig';
import { ActionButtonsConfig } from '@/components/feature-configs/action-button/ActionButtonsConfig';
import { PlaybackSpeedConfig } from '@/components/feature-configs/PlaybackSpeedConfig';
import { AudienceConfig } from '@/components/feature-configs/audience';
import { ContinueWatchingAdvancedConfig } from '@/components/feature-configs/continue-watching';
import { BorderConfig } from '@/components/feature-configs/BorderConfig';
import { LogoConfig } from '@/components/feature-configs/logo/LogoConfig';
import { PixelConfig } from '@/components/feature-configs/PixelConfig';
// OCULTO TEMPORARIAMENTE - import { AffiliateConfig } from '@/components/feature-configs/AffiliateConfig';
import { ProgressBarConfig } from '@/components/feature-configs/progress-bar';
import { RepeatConfig } from '@/components/feature-configs/RepeatConfig';
import { SmartPauseConfig } from '@/components/feature-configs/SmartPauseConfig';
import { RestartAfterEndConfig } from '@/components/feature-configs/RestartAfterEndConfig';
import { SubtitleConfig } from '@/components/feature-configs/SubtitleConfig';
import { MiniHookConfig } from '@/components/feature-configs/mini-hook/MiniHookConfig';
import { ConversionJourneyConfig } from '@/components/feature-configs/conversion-journey/ConversionJourneyConfig';
import { SalesNotificationConfig } from '@/components/feature-configs/SalesNotificationConfig';
import { ResponsivePlayerConfig } from '@/components/feature-configs/responsive-player/ResponsivePlayerConfig';
import { VideoFeedbackConfig } from '@/components/feature-configs/video-feedback/VideoFeedbackConfig';
// OCULTO TEMPORARIAMENTE - import { SyncActionsConfig } from '@/components/feature-configs/sync-actions/SyncActionsConfig';
import { FinalCountdownConfig } from '@/components/feature-configs/final-countdown/FinalCountdownConfig';

import { AutoplayUnifiedConfig } from '@/components/feature-configs/autoplay/AutoplayUnifiedConfig';

interface SideBySideEditorProps {
  activeFeature: string;
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
  modalTitle: string;
  modalSubtitle?: string;
  onClose: () => void;
  onApply: () => void;
}

export const SideBySideEditor: React.FC<SideBySideEditorProps> = ({
  activeFeature,
  features,
  setFeature,
  modalTitle,
  modalSubtitle,
  onClose,
  onApply
}) => {
  useLanguage();
  const { containerRef, isReady } = useLeftAnchoredModal();
  const [hideFooter, setHideFooter] = useState(false);

  // Reset hideFooter whenever the active feature changes
  useEffect(() => {
    setHideFooter(false);
  }, [activeFeature]);

  // Hide footer when in Autoplay A/B Test wizard (only if autoplay is enabled)
  const handleAutoplayTabChange = (tab: string) => {
    const isAutoplayEnabled = features.autoplayOverlay?.enabled ?? false;
    // Esconde o footer apenas se estiver na tab de teste AB E o autoplay estiver ativado
    setHideFooter(tab === 'automatic-test' && isAutoplayEnabled);
  };

  // Hide footer when in Headline A/B Test wizard
  const handleHeadlineTabChange = (tab: string) => {
    setHideFooter(tab === 'abtest');
  };

  // Track current autoplay tab to update footer visibility when autoplay is toggled
  const [currentAutoplayTab, setCurrentAutoplayTab] = useState<string>('basic');

  // Update autoplay tab change handler to also track the current tab
  const handleAutoplayTabChangeWithTracking = (tab: string) => {
    setCurrentAutoplayTab(tab);
    handleAutoplayTabChange(tab);
  };

  // Update footer visibility when autoplay enabled state changes (on automatic-test tab)
  useEffect(() => {
    if (activeFeature === 'autoplayOverlay' && currentAutoplayTab === 'automatic-test') {
      const isAutoplayEnabled = features.autoplayOverlay?.enabled ?? false;
      setHideFooter(isAutoplayEnabled);
    }
  }, [features.autoplayOverlay?.enabled, activeFeature, currentAutoplayTab]);

  // Keyboard shortcuts: Esc to close, Ctrl/Cmd+S to apply
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        onApply();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onApply]);

  // Render feature configuration based on active feature
  const renderFeatureConfig = () => {
    switch (activeFeature) {
      case 'controls':
      case 'autoPlay':
        return <VideoControlsConfigurator features={features} setFeature={setFeature} />;
      case 'loop':
        return <RepeatConfig features={features} setFeature={setFeature} />;
      case 'progressBar':
        return <ProgressBarConfig features={features} setFeature={setFeature} />;
      case 'headline':
        return <HeadlineOverlayConfig features={features} setFeature={setFeature} onActiveTabChange={handleHeadlineTabChange} />;
      case 'thumbnail':
      case 'initialThumbnail':
      case 'pauseThumbnail':
      case 'endThumbnail':
        return <ThumbnailConfig features={features} setFeature={setFeature} />;
      case 'actionButton':
        return <ActionButtonsConfig features={features} setFeature={setFeature} />;
      case 'playbackSpeed':
        return <PlaybackSpeedConfig features={features} setFeature={setFeature} />;
      case 'audience':
        return <AudienceConfig features={features} setFeature={setFeature} />;
      case 'salesNotification':
        return <SalesNotificationConfig features={features} setFeature={setFeature} />;
      case 'finalCountdown':
        return <FinalCountdownConfig features={features} setFeature={setFeature} />;
      case 'smartPause':
        return <SmartPauseConfig features={features} setFeature={setFeature} />;
      case 'haveRestart':
        return <RestartAfterEndConfig features={features} setFeature={setFeature} />;
      case 'subtitles':
        return <SubtitleConfig features={features} setFeature={setFeature} />;
      case 'miniHooks':
        return <MiniHookConfig features={features} setFeature={setFeature} />;
      case 'conversionJourney':
        return <ConversionJourneyConfig features={features} setFeature={setFeature} />;
      case 'continueWatching':
        return <ContinueWatchingAdvancedConfig features={features} setFeature={setFeature} />;
      case 'border':
        return <BorderConfig features={features} setFeature={setFeature} />;
      case 'logo':
        return <LogoConfig features={features} setFeature={setFeature} />;
      case 'pixel':
        return <PixelConfig features={features} setFeature={setFeature} />;
      // OCULTO TEMPORARIAMENTE - Será implementado futuramente
      // case 'affiliate':
      //   return <AffiliateConfig features={features} setFeature={setFeature} />;
      case 'autoplayOverlay':
        return <AutoplayUnifiedConfig features={features} setFeature={setFeature} containerRef={containerRef} onActiveTabChange={handleAutoplayTabChangeWithTracking} />;
      case 'responsivePlayer':
        return <ResponsivePlayerConfig features={features} setFeature={setFeature} />;
      case 'videoFeedback':
        return <VideoFeedbackConfig features={features} setFeature={setFeature} />;
      // OCULTO TEMPORARIAMENTE - Será implementado futuramente
      // case 'syncActions':
      //   return <SyncActionsConfig features={features} setFeature={setFeature} />;
      default:
        return <div>{translateInterfaceText("Configuração não encontrada")}</div>;
    }
  };

  return (
    // O padding do container é o que faz os dois painéis "flutuarem" sobre o
    // canvas; por isso os filhos usam h-full (e não min-h-screen, que somaria
    // ao padding e criaria scroll na página inteira).
    // h-full (e não h-screen): este editor é montado sob dois containers com
    // alturas diferentes — VideoEditorLayout (h-screen) e
    // ModularVideoEditorContent (h-[calc(100vh-80px)]) — e h-screen estouraria
    // 80px no segundo caso.
    <div className="flex w-full h-full animate-fade-in transition-all duration-300 ease-in-out gap-4 p-3 bg-[#F1F4F9] overflow-hidden">
      {/* Painel de Edição — card flutuante */}
      <div
        ref={containerRef}
        className="w-[620px] max-w-[660px] min-w-[560px] h-full flex flex-col shrink-0 overflow-hidden bg-[#F7F9FC] rounded-[16px] border border-[#E1E7F0] shadow-[0_8px_30px_rgba(15,23,42,0.08)]"
      >
        <FeatureConfigHeader title={modalTitle} subtitle={modalSubtitle} onClose={onClose} />

        <div className={`flex-1 min-h-0 pb-6 ${!hideFooter ? 'overflow-y-auto' : ''}`}>
          {renderFeatureConfig()}
        </div>

        {!hideFooter && (
          <FeatureConfigFooter onCancel={onClose} onApply={onApply} />
        )}
      </div>

      {/* Preview Lateral — card flutuante (fluido) */}
      <div className="flex-1 min-w-0 h-full overflow-hidden bg-white rounded-[16px] border border-[#E1E7F0] shadow-[0_8px_30px_rgba(15,23,42,0.08)]">
        <LivePreviewPanel
          features={features}
          activeFeature={activeFeature}
          key={`preview-${activeFeature}-${features.videoUrl || 'no-video'}`}
        />
      </div>
    </div>
  );
};